import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Zap, X, Loader2 } from "lucide-react";
import axios from "axios";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickCats = SITE.categories.slice(0, 12).map((c) => c.title);

export default function QuickQuote() {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("");
  const [qty, setQty] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!cat || !phone.trim() || !name.trim()) {
      toast.error("Naam, phone aur category chahiye.");
      return;
    }
    setLoading(true);
    try {
      const message = `Quick quote — ${cat}${
        qty ? ` × ${qty}` : ""
      }. Kripya price aur availability batao.`;
      await axios.post(`${API}/enquiries`, {
        name: name.trim(),
        phone: phone.trim(),
        email: null,
        category: cat,
        message,
      });
      // Open WhatsApp with pre-filled text — force new top-level window
      const waText = encodeURIComponent(
        `Namaste Giriraj Enterprises! ${name.trim()} bol raha hoon.\nQuick quote chahiye — *${cat}*${
          qty ? ` × ${qty}` : ""
        }.\nPhone: ${phone.trim()}`
      );
      const url = `https://wa.me/${SITE.phoneRaw}?text=${waText}`;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) window.location.href = url;
      toast.success("Enquiry saved. WhatsApp khul gaya!");
      setOpen(false);
      setCat("");
      setQty("");
      setName("");
      setPhone("");
    } catch (err) {
      console.error(err);
      toast.error("Kuch problem hai. Direct WhatsApp try karo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setOpen(true)}
        data-testid="quick-quote-fab"
        aria-label="Quick quote"
        className="fixed bottom-6 right-24 md:bottom-8 md:right-28 z-40 group inline-flex items-center gap-2 rounded-full grad-bg text-white pl-4 pr-2 py-2 text-[12px] font-semibold shadow-[0_20px_40px_-10px_rgba(29,82,196,0.55)] hover:opacity-95 transition-opacity duration-300"
      >
        <Zap className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Quick Quote</span>
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white text-[#0d1117] text-[10px] font-bold ml-1">
          ⚡
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
          >
            <motion.form
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={send}
              data-testid="quick-quote-form"
              className="w-full max-w-lg glass-strong rounded-[26px] p-6 md:p-7 relative"
            >
              <div className="absolute -inset-24 bg-[radial-gradient(circle_at_50%_0%,rgba(34,211,238,0.25),transparent_55%)] pointer-events-none" />
              <div className="relative">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="font-mono-tag text-[10px] uppercase text-[#22d3ee]">
                      ⚡ Quick Quote
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-1">
                      Category chuno, qty batao,{" "}
                      <span className="text-grad-brand">WhatsApp bhej do.</span>
                    </h3>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setOpen(false)}
                    className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <label className="font-mono-tag text-[10px] uppercase text-white/50 block mb-2">
                  Category *
                </label>
                <div className="flex flex-wrap gap-2 mb-5 max-h-[130px] overflow-auto pr-1">
                  {quickCats.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setCat(c)}
                      data-testid={`qq-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-300 ${
                        cat === c
                          ? "bg-[#22d3ee] text-[#0d1117] border-[#22d3ee]"
                          : "bg-transparent text-white/70 border-white/15 hover:border-white/40"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Quantity"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="e.g. 10 pcs"
                    testid="qq-qty"
                  />
                  <Input
                    label="Your name *"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Kunal"
                    testid="qq-name"
                  />
                </div>
                <div className="mt-3">
                  <Input
                    label="Phone / WhatsApp *"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 …"
                    testid="qq-phone"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="qq-submit"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25d366] text-white py-3 text-sm font-semibold hover:bg-[#22c55e] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>Send to WhatsApp →</>
                  )}
                </button>
                <p className="mt-3 text-[11px] text-white/40 text-center">
                  Enquiry Giriraj ke DB mein save hogi + WhatsApp khulega with prefilled msg.
                </p>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="flex flex-col">
      <label className="font-mono-tag text-[10px] uppercase text-white/50 mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-white/[0.04] border border-white/[0.08] focus:border-[#22d3ee] outline-none rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-white/25 transition-colors"
      />
    </div>
  );
}
